import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { RootState } from "@/app/rootReducer";
import {
  selectPinnedItems,
  unpinItem,
  QuickLinkItem,
} from "../slices/quickLinksSlice";
import { getIconByName } from "../utils/iconMap";
import { cn } from "@/lib/utils";

interface QuickLinksProps {
  className?: string;
}

const QuickLinks: React.FC<QuickLinksProps> = ({ className }) => {
  const dispatch = useDispatch();
  const pinnedItems = useSelector((state: RootState) => selectPinnedItems(state));

  const handleUnpin = (e: React.MouseEvent, itemId: string) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(unpinItem(itemId));
  };

  if (pinnedItems.length === 0) {
    return null;
  }

  const isExternalUrl = (href: string) =>
    href.startsWith("http://") || href.startsWith("https://");

  return (
    <div className={cn("flex flex-wrap justify-center gap-3 sm:gap-4", className)}>
      {pinnedItems.map((item: QuickLinkItem) => {
        const IconComponent = getIconByName(item.iconName);
        const isExternal = isExternalUrl(item.href);

        const linkContent = (
          <div className="group relative flex flex-col items-center">
            {/* Icon container - browser favorites style */}
            <div className="relative flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 transition-all duration-200 group-hover:bg-white/20 group-hover:border-white/30 group-hover:scale-105 group-hover:shadow-lg">
              <IconComponent className="h-5 w-5 sm:h-6 sm:w-6 text-white/80 group-hover:text-white transition-colors" />

              {/* Unpin button */}
              <button
                className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-gray-800/90 border border-white/20 text-white/60 opacity-0 transition-all duration-200 group-hover:opacity-100 hover:bg-red-600 hover:text-white hover:border-red-500"
                onClick={(e) => handleUnpin(e, item.id)}
                title="Remove from quick links"
                type="button"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </div>

            {/* Label */}
            <span className="mt-1.5 max-w-[70px] sm:max-w-[80px] truncate text-center text-xs text-white/70 group-hover:text-white transition-colors">
              {item.name}
            </span>
          </div>
        );

        if (isExternal) {
          return (
            <a
              className="focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 rounded-xl"
              href={item.href}
              key={item.id}
              rel="noopener noreferrer"
              target="_blank"
            >
              {linkContent}
            </a>
          );
        }

        return (
          <Link
            className="focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 rounded-xl"
            key={item.id}
            to={item.href}
          >
            {linkContent}
          </Link>
        );
      })}
    </div>
  );
};

export default QuickLinks;
